import { Component, OnInit, inject, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

import {
  FriendRequestService,
  IBlockedUserItem,
} from '../../shared/services/friend-request.service';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';

const PAGE_SIZE = 24;

/**
 * "Blocked" tab on the profile page ( /profile/:userName/blocked ).
 *
 * Always shows the CURRENT LOGGED-IN USER's own blocked list — never anyone
 * else's. The backend endpoint (GET /friend-requests/blocked) is scoped
 * server-side to req.user.id, so there's no userId/userName param that
 * could be swapped to view someone else's blocked users. The route/tab is
 * also only ever rendered when viewing your own profile (see profile.ts),
 * but the privacy guarantee lives in the API, not the UI.
 */
@Component({
  selector: 'app-blocked-users',
  standalone: true,
  imports: [CommonModule, ConfirmationPopup],
  templateUrl: './blocked-users.html',
})
export class BlockedUsers implements OnInit {
  private friendRequestService = inject(FriendRequestService);
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);

  items: IBlockedUserItem[] = [];
  isLoading = false;
  allLoaded = false;
  error: string | null = null;
  private page = 1;
  private total = 0;

  actionPendingFor: string | null = null;

  showUnblockConfirm = false;
  private pendingUnblockItem: IBlockedUserItem | null = null;

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) this.loadNextPage();
  }

  /** Single source of truth for the ui-avatars fallback used across the app. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  trackById(_: number, item: IBlockedUserItem): string {
    return item._id;
  }

  loadNextPage(): void {
    if (this.isLoading || this.allLoaded) return;
    this.isLoading = true;
    this.error = null;
    this.cdr.detectChanges();

    this.friendRequestService.getBlockedUsers(this.page, PAGE_SIZE).subscribe({
      next: (res) => {
        const data = res?.data;
        const newItems = data?.items ?? [];
        this.items = [...this.items, ...newItems];
        this.total = data?.total ?? this.items.length;
        this.page += 1;
        this.isLoading = false;
        this.allLoaded = this.items.length >= this.total || newItems.length === 0;
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = 'Failed to load blocked users. Please try again.';
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  promptUnblock(item: IBlockedUserItem, event: Event): void {
    event.stopPropagation();
    this.pendingUnblockItem = item;
    this.showUnblockConfirm = true;
  }

  confirmUnblock(): void {
    this.showUnblockConfirm = false;
    const item = this.pendingUnblockItem;
    this.pendingUnblockItem = null;
    if (!item) return;

    this.actionPendingFor = item._id;
    this.cdr.detectChanges();

    this.friendRequestService.unblock(item._id).subscribe({
      next: () => {
        this.items = this.items.filter(i => i._id !== item._id);
        this.total = Math.max(0, this.total - 1);
        this.actionPendingFor = null;
        this.friendRequestService.triggerRefresh();
        this.cdr.detectChanges();
      },
      error: () => {
        this.actionPendingFor = null;
        this.cdr.detectChanges();
      },
    });
  }

  cancelUnblockConfirm(): void {
    this.showUnblockConfirm = false;
    this.pendingUnblockItem = null;
  }
}
